import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { UsersPaginationProps } from '../types';

export function UsersPagination({
  isDark,
  currentPage,
  totalPages,
  total,
  itemsPerPage,
  onPageChange,
}: UsersPaginationProps) {
  const showingFrom = ((currentPage - 1) * itemsPerPage) + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, total);

  return (
    <div className={cn(
      'p-4 lg:p-5 border-t flex flex-col sm:flex-row items-center justify-between gap-4',
      isDark ? 'border-slate-700' : 'border-slate-200'
    )}>
      <p className={cn(
        'text-sm',
        isDark ? 'text-slate-400' : 'text-slate-500'
      )}>
        Showing {showingFrom} to {showingTo} of {total} results
      </p>
      
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={cn(
            'h-9 px-3 rounded-lg',
            isDark 
              ? 'border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white disabled:opacity-30' 
              : 'border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30'
          )}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let page;
            if (totalPages <= 5) {
              page = i + 1;
            } else if (currentPage <= 3) {
              page = i + 1;
            } else if (currentPage >= totalPages - 2) {
              page = totalPages - 4 + i;
            } else {
              page = currentPage - 2 + i;
            }
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={cn(
                  'w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200',
                  currentPage === page
                    ? 'bg-[#0EA5A4] text-white shadow-lg shadow-[#0EA5A4]/20'
                    : isDark
                      ? 'text-slate-400 hover:bg-slate-800 hover:text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                )}
              >
                {page}
              </button>
            );
          })}
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={cn(
            'h-9 px-3 rounded-lg',
            isDark 
              ? 'border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white disabled:opacity-30' 
              : 'border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30'
          )}
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
